import React from "react";
import ErrorBoundary from "./components/ErrorBoundary";
import { lightColors, darkColors } from "./components/Theme/WebTheme";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import AuthLoading from "./views/auth/AuthLoading";
import { Provider } from "react-redux";
import { FirebaseProvider, store, api } from "common";
import { FirebaseConfig } from "./config/FirebaseConfig";
import i18n from "i18next";
import { initReactI18next, useTranslation } from "react-i18next";
import { ThemeProvider } from "@mui/material/styles";
import { createTheme, CssBaseline } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/fr";
import "dayjs/locale/en";
import "moment/locale/fr";
import { frFR, enUS } from "@mui/x-date-pickers/locales";
import RecaptchaWrapper from "./components/RecaptchaWrapper";
import GoogleAuthProviderWrapper from "./components/GoogleAuthProviderWrapper";
import { HelmetProvider } from "react-helmet-async";
import bookingEn from "./i18n/booking-en.json";
import bookingFr from "./i18n/booking-fr.json";
import enTranslations from "./i18n/language-en.json";
import frTranslations from "./i18n/language-fr.json";
import LangWrapper from "./components/LangWrapper";
import { toLocalizedPath } from "./utils/i18nRoute"; // [I18N_ROUTE]
import "./styles/datePickerOverrides.css"; // DatePicker dark mode overrides
import "./styles/global.css";
import AppHUD from "./components/AppHUD";
import ScrollToTop from "./components/ScrollToTop";
import { AlertProvider } from "./contexts/AlertContext";
import { MfaProvider } from "./contexts/MfaContext";
import MfaVerifyModal from "./components/auth/MfaVerifyModal";
import FreshMfaModal from "./components/auth/FreshMfaModal";
import { haptic } from "./utils/haptics";
import { ThemeModeContext, useThemeMode } from "./contexts/ThemeModeContext";

i18n.use(initReactI18next).init({
  resources: {
    en: {
      booking: bookingEn,
      translations: enTranslations,
    },
    fr: {
      booking: bookingFr,
      translations: frTranslations,
    },
  },
  fallbackLng: "en",
  ns: ["translations", "booking"],
  defaultNS: "translations",
  fallbackNS: "translations",
  interpolation: {
    escapeValue: false,
  },
});

export { ThemeModeContext, useThemeMode };

function App() {
  const { i18n } = useTranslation(); // Trigger re-render on language change
  // 2. State setup - read themeMode ("light" | "dark" | "system") from localStorage, URL, or default to system
  const [themeMode, setThemeMode] = React.useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const forceTheme = urlParams.get('theme');
    if (forceTheme === 'dark' || forceTheme === 'light') return forceTheme;

    const savedMode = localStorage.getItem("themeMode");
    if (savedMode === "light" || savedMode === "dark" || savedMode === "system") {
      return savedMode;
    }
    const legacySavedMode = localStorage.getItem("darkMode");
    if (legacySavedMode === "true") return "dark";
    if (legacySavedMode === "false") return "light";

    return "system";
  });

  // Track system preference for dark mode
  const [systemPrefersDark, setSystemPrefersDark] = React.useState(() => {
    return (
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
    );
  });

  // Listen to OS-level dark/light mode changes dynamically
  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e) => {
      setSystemPrefersDark(e.matches);
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);

  // Compute effective darkMode boolean for ThemeProvider and CSS
  const darkMode = React.useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const forceTheme = urlParams.get('theme');
    if (forceTheme === 'dark') return true;
    if (forceTheme === 'light') return false;

    if (themeMode === 'dark') return true;
    if (themeMode === 'light') return false;

    // For system mode: directly check current matchMedia
    let isSysDark = false;
    if (typeof window !== "undefined" && window.matchMedia) {
      isSysDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    } else {
      isSysDark = !!systemPrefersDark;
    }
    return isSysDark;
  }, [themeMode, systemPrefersDark]);

  // 3. React to URL changes (if URL changes without reload)
  React.useEffect(() => {
    const checkUrlTheme = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const forceTheme = urlParams.get('theme');
      if (forceTheme === 'dark' && themeMode !== 'dark') setThemeMode('dark');
      if (forceTheme === 'light' && themeMode !== 'light') setThemeMode('light');
    };
    checkUrlTheme();
    window.addEventListener('popstate', checkUrlTheme);
    return () => window.removeEventListener('popstate', checkUrlTheme);
  }, [themeMode]);

  // Global handler for ChunkLoadError (triggered when new deployment deletes old JS chunks)
  React.useEffect(() => {
    const handleUnhandledRejection = (event) => {
      const error = event.reason;
      if (
        error &&
        (error.name === "ChunkLoadError" ||
          (error.message &&
            (error.message.includes("Loading chunk") ||
              error.message.includes("missing:") ||
              error.message.includes("Failed to fetch dynamically imported module"))))
      ) {
        const pageHasAlreadyBeenReloaded = sessionStorage.getItem("chunk_has_reloaded");
        if (!pageHasAlreadyBeenReloaded) {
          sessionStorage.setItem("chunk_has_reloaded", "true");
          window.location.reload();
        }
      }
    };
    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    return () => window.removeEventListener("unhandledrejection", handleUnhandledRejection);
  }, []);

  React.useEffect(() => {
    localStorage.setItem("themeMode", themeMode);
    localStorage.setItem("darkMode", darkMode ? "true" : "false");
    const themeColors = darkMode ? darkColors : lightColors;
    const root = document.documentElement;

    // Inject colors as CSS variables
    Object.keys(themeColors).forEach((key) => {
      if (typeof themeColors[key] === "object" && themeColors[key] !== null) {
        Object.keys(themeColors[key]).forEach((subKey) => {
          root.style.setProperty(
            `--${key}-${subKey}`,
            themeColors[key][subKey],
          );
        });
      } else {
        root.style.setProperty(`--${key}`, themeColors[key]);
      }
    });

    // Add/remove dark class on body for CSS targeting
    // [FIX] Override index.html inline style to match theme background
    // [IFRAME HOME] When embed=home, force everything to be transparent and light-scheme
    const embedParam = new URLSearchParams(window.location.search).get('embed');
    if (embedParam === 'home') {
      document.body.style.setProperty('background-color', 'transparent', 'important');
      document.documentElement.style.setProperty('background-color', 'transparent', 'important');
      document.getElementById('root')?.style.setProperty('background-color', 'transparent', 'important');
      document.documentElement.style.setProperty('color-scheme', 'only light', 'important');
    } else {
      document.body.style.backgroundColor = themeColors.LandingPage_Background;
    }

    if (darkMode) {
      document.body.classList.add("dark");
      document.body.setAttribute("data-theme", "dark");
    } else {
      document.body.classList.remove("dark");
      document.body.setAttribute("data-theme", "light");
    }

    // [DIAGNOSTIC] Initialize overflow blocking classes removed as they cause fluid scroll issues on mobile
    // document.documentElement.classList.add("no-x-overflow");
    // document.body.classList.add("no-x-overflow");
    // document.getElementById("root")?.classList.add("no-x-overflow");
  }, [darkMode, themeMode]);

  // [FIX] Hide reCAPTCHA on scroll logic
  React.useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        document.body.classList.add("scrolling");
      } else {
        document.body.classList.remove("scrolling");
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // [CLOCK_TOUCH_NORMALIZATION]
  // Fix touch coordinates when the MUI Clock dial is scaled up via CSS transform on mobile
  React.useEffect(() => {
    const handleClockTouch = (e) => {
      const target = e.target;
      if (!target) return;
      const mask = target.closest ? target.closest('.MuiClock-squareMask') : null;
      if (!mask) return;

      const touch = e.changedTouches?.[0] || e.touches?.[0] || (e.clientX !== undefined ? e : null);
      if (!touch) return;

      const rectReal = Element.prototype.getBoundingClientRect.call(mask);
      const scale = rectReal.width / 220;
      if (!scale || Math.abs(scale - 1) < 0.02) return;

      const touchX = touch.clientX;
      const touchY = touch.clientY;
      const unscaledX = (touchX - rectReal.left) / scale;
      const unscaledY = (touchY - rectReal.top) / scale;

      const fakeLeft = touchX - unscaledX;
      const fakeTop = touchY - unscaledY;

      mask.getBoundingClientRect = function() {
        return {
          left: fakeLeft,
          top: fakeTop,
          width: 220,
          height: 220,
          right: fakeLeft + 220,
          bottom: fakeTop + 220,
          x: fakeLeft,
          y: fakeTop
        };
      };

      try {
        Object.defineProperty(e, 'offsetX', { value: unscaledX, configurable: true, writable: true });
        Object.defineProperty(e, 'offsetY', { value: unscaledY, configurable: true, writable: true });
      } catch (_) {}

      setTimeout(() => {
        try {
          delete mask.getBoundingClientRect;
        } catch (_) {}
      }, 0);
    };

    window.addEventListener('touchstart', handleClockTouch, { capture: true, passive: false });
    window.addEventListener('touchmove', handleClockTouch, { capture: true, passive: false });
    window.addEventListener('touchend', handleClockTouch, { capture: true, passive: false });

    return () => {
      window.removeEventListener('touchstart', handleClockTouch, { capture: true });
      window.removeEventListener('touchmove', handleClockTouch, { capture: true });
      window.removeEventListener('touchend', handleClockTouch, { capture: true });
    };
  }, []);

  // [AUTH_RESET_PW] Ensure password reset links always navigate to /login to trigger ResetPasswordModal
  React.useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const mode = params.get("mode");
      const oobCode = params.get("oobCode");
      const p = window.location.pathname;
      if (mode === "resetPassword" && oobCode && p !== "/login" && p !== "/en/login") {
        const targetPath = p.startsWith("/en") ? "/en/login" : "/login";
        window.location.replace(`${targetPath}${window.location.search}`);
      }
    } catch (_) {}
  }, []);

  // [AUTH_GOOGLE] Handle PWA/Mobile Redirect Flow on App Mount
  React.useEffect(() => {
    // console.log("[AUTH_GOOGLE] App bootstrap mounting.");
    const handleRedirect = async () => {
      try {
        const { checkAuthRedirect, safeGet, safeSet, safeRemove } = api;
        // console.log("[AUTH_GOOGLE] calling checkAuthRedirect...");
        const result = await store.dispatch(checkAuthRedirect());

        if (result && result.success) {
          if (result.reason === "initial_idle") {
            // console.log("[AUTH_GOOGLE] bootstrap: idle (no active redirect).");
          } else {
            // console.log("[AUTH_GOOGLE] bootstrap success. Mode:", result.mode);
          }

          // Accept all redirect-like modes
          const shouldForceNav =
            result.isRedirect ||
            ["redirectResult", "restored_post_error", "restored_post_missing_state"].includes(result.mode);

          if (shouldForceNav) {
            const source = safeGet("AUTH_SOURCE_V1");
            const returnPath = safeGet("POST_AUTH_RETURN");
            let target = "/";

            if ((source === "booking" || source === "public") && returnPath) {
              target = returnPath;
              if (source === "booking") safeSet("AUTH_SOURCE_V1", "booking");
              else safeRemove("AUTH_SOURCE_V1");
            } else {
              safeRemove("AUTH_SOURCE_V1");
            }

            safeRemove("POST_AUTH_RETURN");
            safeRemove("GOOGLE_RETRY_NEEDED");

            const localizedTarget = toLocalizedPath(target, window.location.pathname);

            // Avoid pointless reload loops
            const current = window.location.pathname + window.location.search;
            if (!current.startsWith(localizedTarget)) {
              const separator = localizedTarget.includes("?") ? "&" : "?";
              // console.log("[AUTH_GOOGLE] forcing redirect to:", localizedTarget);
              window.location.replace(localizedTarget + separator + "login_success=1");
            } else {
              // console.log("[AUTH_GOOGLE] already on target, skip replace.");
            }
          }
        } else {
          console.warn("[AUTH_GOOGLE] bootstrap failed:", result?.reason || "unknown");
        }
      } catch (e) {
        console.error("[AUTH_GOOGLE] bootstrap crash:", e);
      }
    };
    handleRedirect();
  }, []);

  const colors = darkMode ? darkColors : lightColors;

  const theme = createTheme({
    palette: {
      mode: darkMode ? "dark" : "light",
      primary: {
        main: colors.Button_Primary,
        contrastText: "#ffffff",
      },
      secondary: {
        main: colors.Button_Secondary,
        contrastText: darkMode ? "#000000" : "#ffffff",
      },
      text: {
        primary: colors.Header_Content,
        secondary: colors.LandingPage_Description,
      },
      background: {
        default: (new URLSearchParams(window.location.search).get('embed') === 'home') ? 'transparent' : colors.LandingPage_Background,
        paper: (new URLSearchParams(window.location.search).get('embed') === 'home') ? 'transparent' : colors.White,
      },
      brand: {
        yellow: colors.YELLOW,
        orange: colors.ORANGE,
        red: colors.RED,
        green: colors.GREEN,
        black: colors.BLACK,
        white: colors.WHITE,
      },
    },
    typography: {
      fontFamily: [
        "Inter",
        "Roboto",
        '"Helvetica Neue"',
        "Arial",
        "sans-serif",
      ].join(","),
      h1: { fontWeight: 700, color: colors.Header_Content },
      h2: { fontWeight: 600, color: colors.Header_Content },
      h3: { fontWeight: 600, color: colors.Header_Content },
      h4: { fontWeight: 600, color: colors.Header_Content },
      h5: { fontWeight: 500, color: colors.Header_Content },
      h6: { fontWeight: 500, color: colors.Header_Content },
      body1: { color: colors.LandingPage_Description },
      button: { fontWeight: 600, textTransform: "none" },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            padding: "10px 24px",
            boxShadow: "none",
            "&:hover": {
              boxShadow: darkMode
                ? "0px 4px 12px rgba(255, 255, 255, 0.1)"
                : "0px 4px 12px rgba(59, 130, 246, 0.2)",
            },
          },
          containedPrimary: {
            backgroundColor: colors.Button_Primary,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: darkMode ? "#1E293B" : "#FFFFFF",
            borderRadius: "12px",
            boxShadow: darkMode
              ? "0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)"
              : "0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.02)",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          rounded: {
            backgroundColor: darkMode ? "#1E293B" : "#FFFFFF",
            borderRadius: "12px",
            boxShadow: darkMode
              ? "0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)"
              : "0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.02)",
          },
          elevation1: {
            boxShadow: darkMode
              ? "0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)"
              : "0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.02)",
          },
          elevation3: {
            boxShadow: darkMode
              ? "0 10px 15px -3px rgba(0, 0, 0, 0.4), 0 4px 6px -2px rgba(0, 0, 0, 0.3)"
              : "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: 12,
            },
          },
        },
      },
      MuiTimePickerToolbar: {
        styleOverrides: {
          ampmLabel: {
            fontSize: "1rem", // Smaller
            fontWeight: 700,
            color: darkMode ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)",
            "&.Mui-selected": {
              fontSize: "1.1rem", // Smaller
              color: "#d4af37 !important",
            },
          },
        },
      },
      MuiPickersToolbarText: {
        styleOverrides: {
          root: {
            fontSize: "1.5rem", // Compact
            color: darkMode ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)",
            "&.Mui-selected": {
              color: "#d4af37 !important",
            },
          },
        },
      },
      // ========== DATE PICKER DARK MODE STYLES ==========
      MuiPickersLayout: {
        styleOverrides: {
          root: {
            backgroundColor: darkMode ? "#18181B" : "#FFFFFF",
            color: darkMode ? "#FFFFFF" : "#000000",
          },
        },
      },
      MuiDateCalendar: {
        styleOverrides: {
          root: {
            backgroundColor: darkMode ? "#18181B" : "#FFFFFF",
            color: darkMode ? "#FFFFFF" : "#000000",
            margin: "0 auto !important",
            width: "100%",
            maxWidth: "320px !important",
            height: "auto !important",
            maxHeight: "none !important",
            minHeight: "280px !important",
            display: "block",
          },
        },
      },
      MuiPickersCalendarHeader: {
        styleOverrides: {
          label: {
            color: darkMode ? "#FFFFFF" : "#000000",
          },
          switchViewIcon: {
            color: darkMode ? "#FFFFFF" : "#000000",
          },
        },
      },
      MuiDayCalendar: {
        styleOverrides: {
          weekDayLabel: {
            color: darkMode ? "#A1A1AA" : "#71717A",
          },
        },
      },
      MuiPickersDay: {
        styleOverrides: {
          root: {
            color: darkMode ? "#FFFFFF" : "#000000",
            backgroundColor: "transparent",
            "&.Mui-selected": {
              backgroundColor: "#d4af37 !important",
              color: "#000000 !important",
            },
            "&:hover": {
              backgroundColor: darkMode
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.05)",
            },
            "&.Mui-disabled": {
              color: darkMode ? "#555555" : "#cccccc",
            },
          },
        },
      },
      MuiTimeClock: {
        styleOverrides: {
          root: {
            backgroundColor: darkMode ? "#18181B" : "#FFFFFF",
          },
        },
      },
      MuiClockNumber: {
        styleOverrides: {
          root: {
            color: darkMode ? "#FFFFFF" : "#000000",
            pointerEvents: "none",
            "&.Mui-selected": {
              backgroundColor: "#d4af37 !important",
              color: "#000000 !important",
            },
          },
        },
      },
      MuiClockPointer: {
        styleOverrides: {
          root: {
            backgroundColor: "#d4af37",
            pointerEvents: "none",
          },
          thumb: {
            backgroundColor: "#d4af37",
            borderColor: "#d4af37",
            pointerEvents: "none",
          },
        },
      },
      MuiClock: {
        styleOverrides: {
          pin: {
            backgroundColor: "#d4af37",
            pointerEvents: "none",
          },
          wrapper: {
            pointerEvents: "none",
          },
          squareMask: {
            pointerEvents: "auto",
            zIndex: 10,
            touchAction: "none",
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            "& .MuiButton-text, & .MuiButton-outlined": {
              color: "#d4af37",
            },
          },
        },
      },
      MuiPickersToolbar: {
        styleOverrides: {
          root: {
            backgroundColor: darkMode ? "#18181B" : "#FFFFFF",
            color: darkMode ? "#FFFFFF" : "#000000",
          },
        },
      },
      MuiDateTimePickerToolbar: {
        styleOverrides: {
          root: {
            backgroundColor: darkMode ? "#18181B" : "#FFFFFF",
            paddingTop: "8px",
            paddingBottom: "8px",
            "& .MuiTypography-root": {
              color: darkMode ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.6)",
              fontSize: "1.2rem", // Smaller baseline
              "&.Mui-selected": {
                color: "#d4af37 !important",
                fontSize: "1.3rem",
              }
            },
          },
        },
      },
      // Critical: Style the Dialog Paper (modal wrapper) for dark mode
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: darkMode ? "#18181B" : "#FFFFFF",
            color: darkMode ? "#FFFFFF" : "#000000",
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            backgroundColor: darkMode ? "#18181B" : "#FFFFFF",
          },
        },
      },

      // Override the MultiSectionDigitalClock for time selection
      MuiMultiSectionDigitalClock: {
        styleOverrides: {
          root: {
            backgroundColor: darkMode ? "#18181B" : "#FFFFFF",
            "& .MuiList-root": {
              backgroundColor: darkMode ? "#18181B" : "#FFFFFF",
            },
            "& .MuiMenuItem-root": {
              color: darkMode ? "#FFFFFF" : "#000000",
              "&.Mui-selected": {
                backgroundColor: "#d4af37 !important",
                color: "#000000 !important",
              },
              "&:hover": {
                backgroundColor: darkMode
                  ? "rgba(255, 255, 255, 0.1)"
                  : "rgba(0, 0, 0, 0.04)",
              },
            },
          },
        },
      },
      MuiDigitalClock: {
        styleOverrides: {
          root: {
            backgroundColor: darkMode ? "#18181B" : "#FFFFFF",
          },
          item: {
            color: darkMode ? "#FFFFFF" : "#000000",
            "&.Mui-selected": {
              backgroundColor: "#d4af37 !important",
              color: "#000000 !important",
            },
          },
        },
      },
    },
  });

  const toggleDarkMode = () => {
    haptic.light();
    setThemeMode((prevMode) => {
      let nextMode;
      // Cycle: light (☀️) -> dark (🌙) -> system (🖥️) -> light (☀️)
      if (prevMode === "light") {
        nextMode = "dark";
      } else if (prevMode === "dark") {
        nextMode = "system";
      } else {
        nextMode = "light";
      }
      return nextMode;
    });
  };

  const themeModeContextValue = React.useMemo(
    () => ({
      themeMode,
      darkMode,
      toggleDarkMode,
      setThemeMode,
    }),
    [themeMode, darkMode]
  );

  return (
    <HelmetProvider>
      <ErrorBoundary>
        <Provider store={store}>
          <FirebaseProvider config={FirebaseConfig} api={api}>
            <GoogleAuthProviderWrapper>
              <ThemeModeContext.Provider value={themeModeContextValue}>
                <AlertProvider>
                  <MfaProvider>
                    <ThemeProvider theme={theme}>
                      <CssBaseline />
                      <LocalizationProvider
                        dateAdapter={AdapterDayjs}
                        adapterLocale={i18n.language}
                        localeText={
                          (i18n.language === "fr" ? frFR : enUS).components
                            .MuiLocalizationProvider.defaultProps.localeText
                        }
                      >
                        <AppHUD />
                        <AuthLoading>
                          <RecaptchaWrapper>
                            <BrowserRouter>
                            <ScrollToTop />
                            <MfaVerifyModal />
                            <FreshMfaModal />
                            <Routes>
                            {/* English Route with /en prefix */}
                            <Route
                              path="/en/*"
                              element={
                                <LangWrapper
                                  lang="en"
                                  darkMode={darkMode}
                                  toggleDarkMode={toggleDarkMode}
                                  themeMode={themeMode}
                                />
                              }
                            />

                            {/* Default Route (French) with no prefix - capture everything else */}
                            <Route
                              path="/*"
                              element={
                                <LangWrapper
                                  defaultLang="fr"
                                  darkMode={darkMode}
                                  toggleDarkMode={toggleDarkMode}
                                  themeMode={themeMode}
                                />
                              }
                            />
                          </Routes>
                          </BrowserRouter>
                        </RecaptchaWrapper>
                      </AuthLoading>
                    </LocalizationProvider>
                  </ThemeProvider>
                </MfaProvider>
              </AlertProvider>
            </ThemeModeContext.Provider>
            </GoogleAuthProviderWrapper>
          </FirebaseProvider>
        </Provider>
      </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;
